import $ from 'jquery';
import _ from 'lodash';
import Swal from 'sweetalert2';
import { renderLoader } from '~loader/renderLoader';
import { getAxiosClient } from '~src/api';
import { onErrorShowMessage } from '~src/errors';
import { isLoggedUserGlobalEditor } from '~src/user';
import {
  enableLoaderInput,
  disableLoaderInput,
  createBlockSticker,
  loadingBox,
} from '~loader/loader-utils';
import { unmarshalGraphqlEdges } from '~graphql/utils';

const findTemplateById = async (templateId) => {
  let templates = [];
  templates = window.editorConfig.sidebarTemplates;

  const templateToDuplicate = templates.items.find((template) => {
    if (template.objectId === templateId) {
      return template;
    }
  });
  return templateToDuplicate;
};

const saveDuplicatedTemplate = async (templateToDuplicate) => {
  const httpClient = await getAxiosClient();

  const isGlobalEditor = await isLoggedUserGlobalEditor();

  const name = isGlobalEditor ? '' : 'Modello Personalizzato';

  const data = {
    name,
  };

  let tableName;

  if (isGlobalEditor) {
    data.isGlobal = true;
    tableName = 'editor_templates_default';
  } else {
    data.isGlobal = false;
    tableName = 'editor_templates';
  }

  if (templateToDuplicate.editor_templates_categories_default_pointer) {
    data.editor_templates_categories_default_pointer =
      templateToDuplicate.editor_templates_categories_default_pointer.objectId;
  }

  if (templateToDuplicate.editor_templates_categories_pointer) {
    data.editor_templates_categories_pointer =
      templateToDuplicate.editor_templates_categories_pointer.objectId;
  }

  if (templateToDuplicate.editor_project_types_default_pointer) {
    data.editor_project_types_default_pointer =
      templateToDuplicate.editor_project_types_default_pointer.objectId;
  }

  if (templateToDuplicate.editor_project_types_pointer) {
    data.editor_project_types_pointer =
      templateToDuplicate.editor_project_types_pointer.objectId;
  }

  if (templateToDuplicate.editor_project_formats_default_pointer) {
    data.editor_project_formats_default_pointer =
      templateToDuplicate.editor_project_formats_default_pointer.objectId;
  }

  if (templateToDuplicate.editor_project_formats_pointer) {
    data.editor_project_formats_pointer =
      templateToDuplicate.editor_project_formats_pointer.objectId;
  }

  if (templateToDuplicate.metadata) {
    data.metadata = templateToDuplicate.metadata;
  }

  const response = await httpClient.post(
    `${process.env.PARSE_API_URL}/${tableName}`,
    {
      data,
    }
  );

  return response.data.data;
};

const transformPointerObjectsToIds = (object) => {
  for (const objectProperty of Object.keys(object)) {
    if (objectProperty.includes('_pointer')) {
      if (!object[objectProperty]) {
        continue;
      }
      object[objectProperty] = object[objectProperty].objectId;
    }
  }
  return object;
};

const saveDuplicateTemplatePages = async (
  templateToDuplicate,
  newTemplate = {}
) => {
  const httpClient = await getAxiosClient();

  let tableName;

  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default_pages';
  } else {
    tableName = 'editor_templates_pages';
  }
  let templatePages;

  if (templateToDuplicate.isGlobal) {
    templatePages = unmarshalGraphqlEdges(
      templateToDuplicate.editor_templates_default_pages
    );
  } else {
    templatePages = unmarshalGraphqlEdges(
      templateToDuplicate.editor_templates_pages
    );
  }
  templatePages = templatePages.edges;

  const dataTemplatePages = templatePages.map((templatePage) => {
    delete templatePage.objectId;
    delete templatePage.createdAt;
    delete templatePage.updatedAt;
    delete templatePage.org_pointer;
    if (templatePage.preview) {
      templatePage.preview.__type = 'File'; // Required to allow saving preview via PUT in lambda-parse-api
    }

    if (!isGlobalEditor) {
      delete templatePage.isGlobal;
    }

    if (newTemplate.isGlobal) {
      templatePage.editor_templates_default_pointer = newTemplate.objectId;
    } else {
      templatePage.editor_templates_pointer = newTemplate.objectId;
    }

    return transformPointerObjectsToIds(templatePage);
  });

  const promises = [];
  for (const dataTemplatePage of dataTemplatePages) {
    const promise = httpClient.post(
      `${process.env.PARSE_API_URL}/${tableName}`,
      {
        data: dataTemplatePage,
      }
    );
    promises.push(promise);
  }
  const responses = await Promise.all(promises);

  return responses;
};

const saveDuplicatedTemplateAndPagesRelation = async (templateId, pagesIds) => {
  const httpClient = await getAxiosClient();

  let tableName;
  let relationClassName;
  let relationColumnName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_templates_default';
    relationClassName = 'editor_templates_default_pages';
    relationColumnName = 'editor_templates_default_pages';
  } else {
    tableName = 'editor_templates';
    relationClassName = 'editor_templates_pages';
    relationColumnName = 'editor_templates_pages';
  }

  const url = `${process.env.PARSE_API_URL}/${tableName}/${templateId}`;
  const method = 'PUT';
  const data = {};

  data.relations = [
    {
      class: relationClassName,
      column: relationColumnName,
      toIds: pagesIds,
    },
  ];

  return httpClient({ url, method, data: { data }, responseType: 'json' });
};

export const duplicateTemplate = async (templateId) => {
  disableLoaderInput();

  try {
    loadingBox('Duplico il modello scelto...');
    const templateToDuplicate = await findTemplateById(templateId);
    const template = await saveDuplicatedTemplate(templateToDuplicate);
    const pagesResponses = await saveDuplicateTemplatePages(
      templateToDuplicate,
      template
    );
    const duplicatedPageIds = pagesResponses.map((pagesResponse) => {
      const duplicatedPageId = _.get(pagesResponse, 'data.data.objectId');
      return duplicatedPageId;
    });

    await saveDuplicatedTemplateAndPagesRelation(
      template.objectId,
      duplicatedPageIds
    );
    window.editorLoader.templates = {};

    await renderLoader();

    const newTemplateSticker = createBlockSticker('Modello duplicato');
    $(`#template-${template.objectId}`).append(newTemplateSticker);

    Swal.fire({
      title: 'Duplicato!',
      html:
        'Il nuovo modello &egrave; stato duplicato ed è ora pronto per essere utilizzato.',
      icon: 'success',
      confirmButtonColor: '#278e8e',
    });
  } catch (error) {
    enableLoaderInput();
    console.error(error);
    onErrorShowMessage();
  }
};
